import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Spinner } from "../../components/ui/Spinner";
import { useApi } from "../../context/ApiContext";
import { DashboardProgressIndicator } from "../../components/admin/DashboardProgressIndicator";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { api } = useApi();
  const [stats, setStats] = useState<any>(null);
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [salesByDay, setSalesByDay] = useState<any[]>([]);
  const [financialOverview, setFinancialOverview] = useState<any>(null);
  const [usage, setUsage] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    checkFirstLogin();
  }, []);

  const checkFirstLogin = async () => {
    try {
      const response = await api.get("/store/setup-progress");
      const progress = response.data;
      // If progress is very low (< 20%), redirect to wizard
      if (progress.overallProgress < 20) {
        navigate("/admin/setup-wizard");
      }
    } catch (err) {
      // Ignore errors, just don't redirect
      console.error("Failed to check setup progress:", err);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin/dashboard/stats");
      setStats(response.data.stats);
      setRecentOrders(response.data.recentOrders || []);
      setSalesByDay(response.data.salesByDay || []);
      setFinancialOverview(response.data.financialOverview);
      setUsage(response.data.usage);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Stack align="center" spacing="lg">
          <Spinner size="lg" />
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="lg">
        <Heading size="xl">{t("admin.dashboard.title")}</Heading>

        {/* Setup Progress Indicator - Only show if not 100% complete */}
        <DashboardProgressIndicator onOpenWizard={() => navigate("/admin/setup-wizard")} />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card padding="md">
            <Stack spacing="sm">
              <div className="text-sm text-gray-500">{t("admin.dashboard.totalOrders")}</div>
              <div className="text-3xl font-bold">{stats?.totalOrders || 0}</div>
            </Stack>
          </Card>
          <Card padding="md">
            <Stack spacing="sm">
              <div className="text-sm text-gray-500">{t("admin.dashboard.totalRevenue")}</div>
              <div className="text-3xl font-bold">
                ${(stats?.totalRevenue || 0).toFixed(2)}
              </div>
            </Stack>
          </Card>
          <Card padding="md">
            <Stack spacing="sm">
              <div className="text-sm text-gray-500">{t("admin.dashboard.totalProducts")}</div>
              <div className="text-3xl font-bold">{stats?.totalProducts || 0}</div>
            </Stack>
          </Card>
          <Card padding="md">
            <Stack spacing="sm">
              <div className="text-sm text-gray-500">{t("admin.dashboard.openConversations")}</div>
              <div className="text-3xl font-bold">{stats?.openConversations || 0}</div>
            </Stack>
          </Card>
        </div>

        {/* Usage & Limits Summary */}
        {usage && (
          <Card padding="lg">
            <Stack spacing="md">
              <Heading size="lg">{t("admin.dashboard.usageAndLimits")}</Heading>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div>
                  <div className="text-sm font-medium text-gray-500">{t("admin.dashboard.products")}</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {usage.usage?.products || 0} / {usage.limits?.products ?? t("admin.dashboard.unlimited")}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">{t("admin.dashboard.orders")}</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {usage.usage?.orders || 0} / {usage.limits?.orders ?? t("admin.dashboard.unlimited")}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">{t("admin.dashboard.pages")}</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {usage.usage?.pages || 0} / {usage.limits?.pages ?? t("admin.dashboard.unlimited")}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">{t("admin.dashboard.categories")}</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {usage.usage?.categories || 0} / {usage.limits?.categories ?? t("admin.dashboard.unlimited")}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">{t("admin.dashboard.users")}</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {usage.usage?.users || 0} / {usage.limits?.users ?? t("admin.dashboard.unlimited")}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">{t("admin.dashboard.mediaSpace")}</div>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {usage.usage?.mediaSpaceInMB 
                      ? `${usage.usage.mediaSpaceInMB.toFixed(2)} ${t("common.mb")}`
                      : usage.usage?.mediaSpace 
                      ? `${(usage.usage.mediaSpace / (1024 * 1024)).toFixed(2)} ${t("common.mb")}`
                      : `0 ${t("common.mb")}`} /{" "}
                    {usage.limits?.mediaSpace 
                      ? `${(usage.limits.mediaSpace / (1024 * 1024)).toFixed(2)} ${t("common.mb")}`
                      : usage.mediaSpaceLimitInMB
                      ? `${usage.mediaSpaceLimitInMB.toFixed(2)} ${t("common.mb")}`
                      : t("admin.dashboard.unlimited")}
                  </div>
                  {usage.mediaSpaceUsagePercentage !== undefined && (
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            usage.mediaSpaceUsagePercentage >= 90
                              ? "bg-red-500"
                              : usage.mediaSpaceUsagePercentage >= 75
                              ? "bg-yellow-500"
                              : "bg-green-500"
                          }`}
                          style={{ width: `${Math.min(usage.mediaSpaceUsagePercentage, 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {usage.mediaSpaceUsagePercentage}% {t("admin.dashboard.used")}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Stack>
          </Card>
        )}

        {/* Financial Overview */}
        {financialOverview && (
          <Card padding="lg">
            <Stack spacing="md">
              <Heading size="lg">{t("admin.dashboard.financialOverview")}</Heading>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 bg-green-50 border border-green-200">
                  <div className="text-sm font-medium text-green-700">{t("admin.dashboard.availableRevenue")}</div>
                  <div className="text-2xl font-bold text-green-900">
                    ${financialOverview.revenue.available.toFixed(2)} {financialOverview.revenue.currency}
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    {t("admin.dashboard.pendingRevenue")}: ${financialOverview.revenue.pending.toFixed(2)}
                  </div>
                </div>
                <div className="p-4 bg-red-50 border border-red-200">
                  <div className="text-sm font-medium text-red-700">{t("admin.dashboard.totalFees")}</div>
                  <div className="text-2xl font-bold text-red-900">
                    ${financialOverview.fees.total.toFixed(2)} {financialOverview.fees.currency}
                  </div>
                  <div className="text-xs text-red-600 mt-1">
                    {t("admin.dashboard.platformFees")}: ${financialOverview.fees.platformFees.toFixed(2)} | 
                    {t("admin.dashboard.subscriptionFees")}: ${financialOverview.fees.subscriptionFees.toFixed(2)}
                  </div>
                </div>
                <div className={`p-4 border ${
                  financialOverview.payout.isPositive 
                    ? "bg-blue-50 border-blue-200" 
                    : "bg-orange-50 border-orange-200"
                }`}>
                  <div className={`text-sm font-medium ${
                    financialOverview.payout.isPositive ? "text-blue-700" : "text-orange-700"
                  }`}>
                    {t("admin.dashboard.netPayout")}
                  </div>
                  <div className={`text-2xl font-bold ${
                    financialOverview.payout.isPositive ? "text-blue-900" : "text-orange-900"
                  }`}>
                    {financialOverview.payout.isPositive ? "+" : "-"}
                    ${Math.abs(financialOverview.payout.netAmount).toFixed(2)} {financialOverview.payout.currency}
                  </div>
                  {financialOverview.payout.nextPayoutDate && (
                    <div className={`text-xs mt-1 ${
                      financialOverview.payout.isPositive ? "text-blue-600" : "text-orange-600"
                    }`}>
                      {t("admin.dashboard.next")}: {new Date(financialOverview.payout.nextPayoutDate).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <div className={`p-4 border ${
                  financialOverview.balance.current >= 0 
                    ? "bg-blue-50 border-indigo-200" 
                    : "bg-yellow-50 border-yellow-200"
                }`}>
                  <div className={`text-sm font-medium ${
                    financialOverview.balance.current >= 0 ? "text-indigo-700" : "text-yellow-700"
                  }`}>
                    {t("admin.dashboard.currentBalance")}
                  </div>
                  <div className={`text-2xl font-bold ${
                    financialOverview.balance.current >= 0 ? "text-indigo-900" : "text-yellow-900"
                  }`}>
                    {financialOverview.balance.current >= 0 ? "+" : "-"}
                    ${Math.abs(financialOverview.balance.current).toFixed(2)} {financialOverview.balance.currency}
                  </div>
                  {financialOverview.balance.current < 0 && (
                    <div className="text-xs text-yellow-600 mt-1">
                      {t("admin.dashboard.paymentRequired")}
                    </div>
                  )}
                </div>
              </div>
              {financialOverview.subscription.billingCycle && (
                <div className="mt-4 p-4 bg-gray-50">
                  <div className="text-sm font-medium text-gray-700">{t("admin.dashboard.subscription")}</div>
                  <div className="text-lg font-semibold text-gray-900">
                    ${financialOverview.subscription.currentFee.toFixed(2)}/{financialOverview.subscription.billingCycle === "yearly" ? t("admin.dashboard.year") : t("admin.dashboard.month")}
                  </div>
                  {financialOverview.subscription.nextBillingDate && (
                    <div className="text-xs text-gray-600 mt-1">
                      {t("admin.dashboard.nextBilling")}: {new Date(financialOverview.subscription.nextBillingDate).toLocaleDateString()}
                    </div>
                  )}
                </div>
              )}
            </Stack>
          </Card>
        )}

        {/* Sales Chart */}
        {salesByDay.length > 0 ? (
          <Card padding="lg">
            <Stack spacing="md">
              <Heading size="lg">{t("admin.dashboard.salesChart")}</Heading>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={salesByDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="_id" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total" stroke="#8884d8" name={t("admin.dashboard.totalRevenue")} />
                  <Line type="monotone" dataKey="count" stroke="#82ca9d" name={t("admin.dashboard.totalOrders")} />
                </LineChart>
              </ResponsiveContainer>
            </Stack>
          </Card>
        ) : (
          <Card padding="lg">
            <Stack spacing="md">
              <Heading size="lg">{t("admin.dashboard.salesChart")}</Heading>
              <div className="text-center py-8 text-gray-500">{t("admin.dashboard.noSalesData")}</div>
            </Stack>
          </Card>
        )}

        {/* Recent Orders */}
        <Card padding="lg">
          <Stack spacing="md">
            <Heading size="lg">{t("admin.dashboard.recentOrders")}</Heading>
            {recentOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">{t("admin.dashboard.noOrders")}</div>
            ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t("admin.orders.orderNumber")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t("admin.orders.customer")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t("admin.orders.total")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t("admin.orders.status")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t("admin.orders.date")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentOrders.map((order: any) => (
                    <tr key={order._id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {order.orderNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.user?.name || order.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        ${order.total.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold ${
                            order.status === "delivered"
                              ? "bg-green-100 text-green-800"
                              : order.status === "processing"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {t(`orders.${order.status}`) || order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
};

export default Dashboard;

